import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  MenuItem,
  Select,
  Skeleton,
  Alert,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ChevronRight,
  Download as DownloadIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { billingApi } from '../api/billingApi';
import {
  SubscriptionDto,
  PaymentDueDto,
  OrderInvoiceDto,
} from '../types';

type BillingTab = 'subscription' | 'payments-due' | 'orders-invoices';

const tabs: { key: BillingTab; label: string }[] = [
  { key: 'subscription', label: 'Subscription' },
  { key: 'payments-due', label: 'Payments Due' },
  { key: 'orders-invoices', label: 'Orders and Invoices' },
];

// ─── Subscription Tab Content ────────────────────────────────────────────────
const SubscriptionTab: React.FC = () => {
  const [subscription, setSubscription] = useState<SubscriptionDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [submittingCancellation, setSubmittingCancellation] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await billingApi.getSubscription();
        setSubscription(data);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load subscription details');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleCancellation = async () => {
    if (subscription?.hasActiveCancellationRequest) {
      toast.warning('Account cancellation request already exists.');
      setCancelDialogOpen(false);
      return;
    }

    if (!cancelReason.trim()) {
      toast.warning('Please provide a reason');
      return;
    }
    try {
      setSubmittingCancellation(true);
      await billingApi.requestCancellation({ reason: cancelReason });
      toast.success('Cancellation request submitted successfully');
      setSubscription((current) =>
        current
          ? {
              ...current,
              hasActiveCancellationRequest: true,
              cancellationRequestedAt: new Date().toISOString(),
            }
          : current
      );
      setCancelDialogOpen(false);
      setCancelReason('');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to submit cancellation request');
    } finally {
      setSubmittingCancellation(false);
    }
  };

  if (loading) {
    return (
      <Box>
        {Array.from({ length: 7 }).map((_, i) => (
          <Skeleton key={i} height={50} sx={{ mb: 0.5 }} />
        ))}
      </Box>
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!subscription) return <Alert severity="info">No subscription data available</Alert>;

  const rows = [
    { label: 'PLAN', value: `${subscription.planName}\n${subscription.description}` },
    {
      label: 'STATUS',
      value: (
        <Chip
          label={subscription.status}
          size="small"
          color={subscription.status === 'Active' ? 'success' : subscription.status === 'Suspended' ? 'error' : 'default'}
        />
      ),
    },
    { label: 'BILLING CYCLE', value: subscription.billingCycle },
    { label: 'ACTIVE EMPLOYEES', value: subscription.activeEmployees },
    { label: 'PURCHASED LICENSES', value: subscription.purchasedLicenses },
    {
      label: 'NEXT BILLING ON',
      value: subscription.nextBillingOn
        ? new Date(subscription.nextBillingOn).toLocaleDateString('en-US', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: '2-digit',
          })
        : '—',
    },
    { label: 'CURRENCY', value: subscription.currency },
  ];

  return (
    <Box>
      {/* Current Subscription Plan */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ color: '#4a6fa5', fontWeight: 500, mb: 0.5 }}>
          Current Subscription Plan
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          The below table shows the details of your current subscription.
        </Typography>

        <Table>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.label} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '0.8rem',
                    color: 'text.primary',
                    width: '40%',
                    letterSpacing: '0.5px',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    py: 2,
                  }}
                >
                  {row.label}
                </TableCell>
                <TableCell
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    py: 2,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {row.value}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Account Cancellation Request */}
      <Paper
        sx={{
          p: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ color: '#4a6fa5', fontWeight: 500, mb: 0.5 }}>
            Account cancellation request
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subscription.hasActiveCancellationRequest
              ? 'Your cancellation request has already been submitted and is under review.'
              : "Your request goes to our customer support team, who'll get in touch with you to take this forward."}
          </Typography>
          {subscription.hasActiveCancellationRequest && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Account cancellation request already exists.
            </Alert>
          )}
        </Box>
        <Button
          variant="outlined"
          color="warning"
          onClick={() => setCancelDialogOpen(true)}
          disabled={subscription.hasActiveCancellationRequest}
          sx={{ borderRadius: 1, textTransform: 'none', minWidth: 180 }}
        >
          {subscription.hasActiveCancellationRequest ? 'Request Submitted' : 'Request Cancellation'}
        </Button>
      </Paper>

      {/* Cancellation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Account Cancellation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please let us know why you'd like to cancel your subscription. Our team will review your request.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason for cancellation"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleCancellation}
            disabled={submittingCancellation || subscription.hasActiveCancellationRequest}
          >
            {submittingCancellation ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─── Payments Due Tab Content ────────────────────────────────────────────────
const PaymentsDueTab: React.FC = () => {
  const [payments, setPayments] = useState<PaymentDueDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offlineDialogOpen, setOfflineDialogOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await billingApi.getPaymentsDue();
        setPayments(data);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load payments due');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ color: '#4a6fa5', fontWeight: 500, mb: 0.5 }}>
              Payments due
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              The below table shows the list of payments due.
            </Typography>
          </Box>
          <Tooltip title="For offline payment queries, contact our support team">
            <Button
              variant="outlined"
              color="info"
              startIcon={<InfoIcon />}
              onClick={() => setOfflineDialogOpen(true)}
              sx={{ textTransform: 'none', borderRadius: 1, whiteSpace: 'nowrap' }}
            >
              Offline Payment?
            </Button>
          </Tooltip>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', letterSpacing: '0.5px' }}>INVOICE ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', letterSpacing: '0.5px' }}>INVOICE DATE</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', letterSpacing: '0.5px' }}>DUE DATE</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', letterSpacing: '0.5px' }}>AMOUNT DUE</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', letterSpacing: '0.5px' }}>STATUS</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', letterSpacing: '0.5px' }}>ACTION</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((_, j) => (
                      <TableCell key={j}><Skeleton /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : payments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No data available in table</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                payments.map((payment) => (
                  <TableRow key={payment.id} hover>
                    <TableCell>{payment.invoiceId}</TableCell>
                    <TableCell>
                      {new Date(payment.invoiceDate).toLocaleDateString('en-US', {
                        weekday: 'short',
                        year: 'numeric',
                        month: 'short',
                        day: '2-digit',
                      })}
                    </TableCell>
                    <TableCell>
                      {new Date(payment.dueDate).toLocaleDateString('en-US', {
                        weekday: 'short',
                        year: 'numeric',
                        month: 'short',
                        day: '2-digit',
                      })}
                    </TableCell>
                    <TableCell>{payment.currency} {payment.amountDue.toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip
                        label={payment.status}
                        size="small"
                        color={
                          payment.status === 'Paid' ? 'success' :
                          payment.status === 'Overdue' ? 'error' :
                          payment.status === 'Pending' ? 'warning' : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined" color="primary" sx={{ textTransform: 'none' }}>
                        Pay Now
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Offline Payment Info Dialog */}
      <Dialog open={offlineDialogOpen} onClose={() => setOfflineDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Offline Payment Information</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            For offline payment queries, please contact our support team at <strong>billing@evitahrms.com</strong>.
            Our team will assist you with bank transfer details and payment confirmation.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOfflineDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─── Orders and Invoices Tab Content ─────────────────────────────────────────
const OrdersInvoicesTab: React.FC = () => {
  const [orders, setOrders] = useState<OrderInvoiceDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number | 'all'>(10);

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      const data = await billingApi.getOrdersAndInvoices({
        page: pageSize === 'all' ? undefined : page,
        pageSize: pageSize === 'all' ? undefined : pageSize,
        search: search || undefined,
      });
      setOrders(data.items);
      setTotalCount(data.totalCount);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load orders and invoices');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleDownload = async (invoiceId: string) => {
    try {
      const blob = await billingApi.downloadInvoice(invoiceId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      toast.error('Failed to download invoice');
    }
  };

  if (error) return <Alert severity="error">{error}</Alert>;

  const totalPages = pageSize === 'all' ? 1 : Math.ceil(totalCount / (pageSize as number));

  return (
    <Box>
      <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ color: '#4a6fa5', fontWeight: 500, mb: 0.5 }}>
          Order and invoice history
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          The below table shows the list of order and invoice history.
        </Typography>

        {/* Controls Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Select
              size="small"
              value={pageSize}
              onChange={(e) => {
                const val = e.target.value;
                setPageSize(val === 'all' ? 'all' : Number(val));
                setPage(1);
              }}
              sx={{ minWidth: 70 }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
            <Typography variant="body2" color="text.secondary">entries per page</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">Search</Typography>
            <TextField
              size="small"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              sx={{ width: 200 }}
            />
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', letterSpacing: '0.5px' }}>INVOICE/RECEIPT ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', letterSpacing: '0.5px' }}>INVOICE DATE</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', letterSpacing: '0.5px' }}>PAYMENT DATE</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', letterSpacing: '0.5px' }}>TRANSACTION AMOUNT</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 4 }).map((_, j) => (
                      <TableCell key={j}><Skeleton /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No data available in table</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {order.invoiceReceiptId}
                        <Tooltip title="Download invoice">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleDownload(order.id)}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {new Date(order.invoiceDate).toLocaleDateString('en-US', {
                        weekday: 'short',
                        year: 'numeric',
                        month: 'short',
                        day: '2-digit',
                      })}
                    </TableCell>
                    <TableCell>
                      {new Date(order.paymentDate).toLocaleDateString('en-US', {
                        weekday: 'short',
                        year: 'numeric',
                        month: 'short',
                        day: '2-digit',
                      })}
                    </TableCell>
                    <TableCell>{order.currency} {order.transactionAmount.toFixed(2)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {orders.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {pageSize === 'all' ? 1 : (page - 1) * (pageSize as number) + 1} to{' '}
              {pageSize === 'all' ? totalCount : Math.min(page * (pageSize as number), totalCount)} of {totalCount}{' '}
              {totalCount === 1 ? 'entry' : 'entries'}
            </Typography>
            {pageSize !== 'all' && totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, val) => setPage(val)}
                color="primary"
                size="small"
              />
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

// ─── Main Billing Page ───────────────────────────────────────────────────────
const BillingPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<BillingTab>('subscription');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const renderContent = () => {
    switch (activeTab) {
      case 'subscription':
        return <SubscriptionTab />;
      case 'payments-due':
        return <PaymentsDueTab />;
      case 'orders-invoices':
        return <OrdersInvoicesTab />;
      default:
        return null;
    }
  };

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Manage Plan & Billing
        </Typography>
        <Box sx={{ width: 40, height: 3, bgcolor: 'warning.main', borderRadius: 2, mt: 0.5 }} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: 3,
          alignItems: 'flex-start',
        }}
      >
        {/* Left Sidebar Tabs */}
        <Paper
          sx={{
            width: isMobile ? '100%' : 280,
            minWidth: isMobile ? '100%' : 280,
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <List disablePadding>
            {tabs.map((tab, index) => (
              <ListItemButton
                key={tab.key}
                selected={activeTab === tab.key}
                onClick={() => setActiveTab(tab.key)}
                sx={{
                  py: 1.8,
                  px: 2.5,
                  borderBottom: index < tabs.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  '&.Mui-selected': {
                    bgcolor: 'rgba(102, 126, 234, 0.15)',
                    color: 'primary.main',
                    fontWeight: 600,
                    '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' },
                  },
                }}
              >
                <ListItemText
                  primary={tab.label}
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: activeTab === tab.key ? 600 : 400,
                  }}
                />
                <ChevronRight sx={{ fontSize: 20, color: 'text.secondary' }} />
              </ListItemButton>
            ))}
          </List>
        </Paper>

        {/* Right Content Area */}
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          {renderContent()}
        </Box>
      </Box>
    </Box>
  );
};

export default BillingPage;
